import { Typography, Button, Form, Divider, ColorPicker } from 'antd';
import { AggregationColor } from 'antd/es/color-picker/color';
import styled from 'styled-components';
import { useNotificationContext } from '@/features/notifications';
import { useUpdateOrganization } from '@/shared/api';
import { useDataInvalidation } from '@/shared/hooks';
import { useRouterContext } from '@/shared/router';
import { DEFAULT_PAGE_BANNER_COLOR } from '@/shared/ui';
import {
  captureError,
  createAggrColorFromGradient,
  getGradientStr,
} from '@/shared/utils';

const { Title, Text } = Typography;

type FormData = {
  bannerColor: AggregationColor;
};

export const OrganizationBannerColor = () => {
  const [form] = Form.useForm();
  const { showNotification } = useNotificationContext();
  const { activeOrganization } = useRouterContext();
  const { mutate: updateOrganization, isLoading: updateOrganizationLoading } =
    useUpdateOrganization();
  const invalidate = useDataInvalidation();

  const handleSubmit = async ({ bannerColor }: FormData) => {
    const theme = { bannerColor: getGradientStr(bannerColor, 224) };
    updateOrganization(
      {
        pathParams: { id: activeOrganization?.id as string },
        body: { theme },
      },
      {
        onSuccess: () => {
          showNotification({
            type: 'success',
            message: 'Organization theme successfully changed',
            description: '',
          });
          invalidate('my-profile');
          invalidate(['organizations', activeOrganization?.id]);
        },
        onError: (error) => {
          showNotification({
            type: 'error',
            message: 'Failed to update organization theme',
            description: error.message,
          });
          captureError(error);
        },
      },
    );
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={{
        bannerColor: createAggrColorFromGradient(
          activeOrganization?.theme?.bannerColor || DEFAULT_PAGE_BANNER_COLOR,
        ),
      }}
    >
      <Divider />
      <FormSection>
        <Title level={4}>Organization Page Banner</Title>
        <Text type="secondary">
          Customize the banner color for your organization&apos;s public page
        </Text>

        <Form.Item
          name="bannerColor"
          label="Banner Color"
          tooltip="Specify a single color or multiple colors with a gradient. To add, delete or change a color, click on bar at the top. To delete a color, click on chosen color, and press the delete key. Change the value and transparency of selected color with bars at the bottom."
        >
          <ColorPicker
            showText
            allowClear={false}
            mode={['single', 'gradient']}
            panelRender={(panel) => (
              <>
                {panel}
                <Button
                  size="small"
                  style={{ marginTop: 10, width: '100%' }}
                  onClick={() =>
                    form.setFieldsValue({
                      bannerColor: createAggrColorFromGradient(
                        DEFAULT_PAGE_BANNER_COLOR,
                      ),
                    })
                  }
                >
                  Reset to Default
                </Button>
              </>
            )}
          />
        </Form.Item>
      </FormSection>

      <ButtonContainer>
        <Button
          type="primary"
          htmlType="submit"
          loading={updateOrganizationLoading}
        >
          Save Changes
        </Button>
      </ButtonContainer>
    </Form>
  );
};

const FormSection = styled.div`
  margin-bottom: 32px;
`;

const ButtonContainer = styled.div`
  margin-top: 24px;
`;
